import { useState } from "react";
import {
  SERVICEBOSS_CONSENT_KEY,
  SERVICEBYREG_CONSENT_KEY,
  SERVICELAD_CONSENT_KEY,
} from "../config/analytics";

const consentCss = `
  .analytics-consent {
    position: fixed; z-index: 10000; display: flex; align-items: center; justify-content: space-between;
    gap: 12px; font-family: system-ui, -apple-system, "Segoe UI", sans-serif;
  }
  .analytics-consent p { margin: 0; font-size: 12px; line-height: 1.35; }
  .analytics-consent a { font-weight: 700; text-underline-offset: 2px; }
  .analytics-consent-actions { display: flex; flex: 0 0 auto; gap: 6px; }
  .analytics-consent button {
    min-height: 28px; padding: 4px 12px; font: inherit; font-size: 12px; font-weight: 700;
    line-height: 1.2; cursor: pointer;
  }
  .analytics-consent button:focus-visible { outline: 2px solid #101418; outline-offset: 2px; }

  /* ServiceByReg: full-width cream strip, grey top line, pill buttons. */
  .analytics-consent.is-servicebyreg {
    right: 0; bottom: 0; left: 0; min-height: 38px; padding: 6px 12px;
    border-top: 1px solid #DDE4D2; background: #f3f8e8; color: #27312d;
    box-shadow: 0 -5px 18px rgba(20, 45, 34, .08);
  }
  .analytics-consent.is-servicebyreg::before {
    position: absolute; top: -2px; right: 0; left: 0; height: 2px;
    background: #DDE4D2; content: "";
  }
  .analytics-consent.is-servicebyreg p { color: #4c5d55; }
  .analytics-consent.is-servicebyreg a { color: #1f6fff; }
  .analytics-consent.is-servicebyreg button {
    min-width: 66px; border: 1px solid #b8cbc2; border-radius: 999px; background: #fff; color: #44564e;
  }
  .analytics-consent.is-servicebyreg button:last-child { border-color: #1f6fff; background: #1f6fff; color: #fff; }

  /* ServiceBoss: dark bar, orange accent, square buttons. */
  .analytics-consent.is-serviceboss {
    right: 0; bottom: 0; left: 0; min-height: 44px; padding: 10px 16px;
    border-top: 3px solid #ff5c35; background: #1c1f21; color: #e8eaec;
    box-shadow: 0 -10px 28px rgba(0, 0, 0, .28);
  }
  .analytics-consent.is-serviceboss p { color: #b0b5b8; font-size: 11px; }
  .analytics-consent.is-serviceboss a { color: #ff8a66; }
  .analytics-consent.is-serviceboss button {
    min-width: 72px; border: 1px solid #5a5f63; border-radius: 5px; background: transparent; color: #f0f1f2;
  }
  .analytics-consent.is-serviceboss button:last-child { border-color: #ff5c35; background: #ff5c35; color: #fff; }

  /* ServiceLad: flat bottom strip, no card chrome. */
  .analytics-consent.is-servicelad {
    right: 0; bottom: 0; left: 0; width: auto; min-height: 0;
    padding: 5px 12px; border: 0; border-top: 1px solid #3b82f6;
    border-radius: 0; background: #eef4fb; color: #1a2a3a;
    box-shadow: none; gap: 8px;
  }
  .analytics-consent.is-servicelad p { color: #3d5166; font-size: 11px; line-height: 1.2; }
  .analytics-consent.is-servicelad a { color: #3b82f6; }
  .analytics-consent.is-servicelad button {
    min-width: 56px; min-height: 24px; padding: 2px 10px; border: 1px solid #b8cfe8;
    border-radius: 999px; background: #fff; color: #2a4a68; font-size: 11px;
  }
  .analytics-consent.is-servicelad button:last-child { border-color: #3b82f6; background: #3b82f6; color: #fff; }

  @media (max-width: 520px) {
    .analytics-consent.is-servicebyreg,
    .analytics-consent.is-serviceboss {
      align-items: flex-start; gap: 8px; padding: 8px 10px;
    }
    .analytics-consent.is-servicebyreg p,
    .analytics-consent.is-serviceboss p { max-width: 210px; }
    .analytics-consent.is-servicebyreg button,
    .analytics-consent.is-serviceboss button { min-width: 58px; padding-inline: 8px; }
    .analytics-consent.is-servicelad {
      right: 0; bottom: 0; left: 0; flex-direction: row; align-items: center; gap: 10px;
    }
    .analytics-consent.is-servicelad .analytics-consent-actions { justify-content: flex-end; }
  }
`;

const BRAND_CONFIG = {
  serviceboss: {
    consentKey: SERVICEBOSS_CONSENT_KEY,
    loadFn: "sbLoadGoogleAnalytics",
    label: "ServiceBoss",
    themeClass: "is-serviceboss",
  },
  servicebyreg: {
    consentKey: SERVICEBYREG_CONSENT_KEY,
    loadFn: "sbrLoadGoogleAnalytics",
    label: "ServiceByReg",
    themeClass: "is-servicebyreg",
  },
  servicelad: {
    consentKey: SERVICELAD_CONSENT_KEY,
    loadFn: "slLoadGoogleAnalytics",
    label: "ServiceLad",
    themeClass: "is-servicelad",
  },
};

const CONSENT_PATHS = new Set(["/", "/about", "/terms", "/privacy"]);

function normalizeConsentPath(pathname) {
  const withoutHtml = pathname.replace(/\.html$/, "");
  const trimmed = withoutHtml.replace(/\/+$/, "");
  return trimmed || "/";
}

function consentAllowed(pathname) {
  return CONSENT_PATHS.has(normalizeConsentPath(pathname));
}

function savedChoice(consentKey) {
  try {
    return window.localStorage.getItem(consentKey);
  } catch {
    return null;
  }
}

export default function AnalyticsConsent({ brandKey }) {
  const config = BRAND_CONFIG[brandKey];
  const [choice, setChoice] = useState(() => (config ? savedChoice(config.consentKey) : null));

  if (!config || choice || !consentAllowed(window.location.pathname)) return null;

  const choose = (granted) => {
    const value = granted ? "granted" : "denied";
    try {
      window.localStorage.setItem(config.consentKey, value);
    } catch {
      // The choice still applies to this page if storage is unavailable.
    }
    window.gtag?.("consent", "update", {
      analytics_storage: value,
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    if (granted) window[config.loadFn]?.();
    setChoice(value);
  };

  return (
    <>
      <style>{consentCss}</style>
      <section
        className={`analytics-consent ${config.themeClass}`}
        role="dialog"
        aria-live="polite"
        aria-label="Analytics cookie choice"
      >
        <p>
          {brandKey === "servicelad"
            ? "We use optional analytics to improve ServiceLad. It is optional, and you can reject it."
            : <>We use optional analytics to improve {config.label}. <a href="/privacy#cookies">Learn more</a></>}
        </p>
        <div className="analytics-consent-actions">
          <button type="button" onClick={() => choose(false)}>Reject</button>
          <button type="button" onClick={() => choose(true)}>Allow</button>
        </div>
      </section>
    </>
  );
}
